import { Checkbox } from "semantic-ui-react";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { firebase } from "astrid-firebase";
import getAcceptedArtifactFiles from "astrid-firestore/src/api/artifacts/getAcceptedArtifactFiles";
import contractorUid from "astrid-firestore/src/api/contractors/utils/contractorUid";

import Flex from "../../../../components/Flex/Flex";
import Button from "../../../ui/components/Buttons/Button";
import PrimaryButton from "../../../ui/components/Buttons/PrimaryButton";
import FileDropzone from "../../../ui/components/FileUpload/FileDropzone";
import FileUploadTable from "../../../ui/components/FileUpload/FileUploadTable";
import ErrorMessage from "../../../ui/components/Messages/ErrorMessage";
import useLoadingFunction from "../../../ui/hooks/useLoadingFunction";
import PageView from "../../../ui/views/PageView/PageView";

import useArtifactFilesUpload from "../../../artifacts/hooks/useArtifactFilesUpload";
import { useUser } from "../../../authentication/state/user";

import ContractorExternalUploadLogin from "./components/ContractorExternalUploadLogin";

const findArticle = firebase.functions().httpsCallable("contractors-findArticle");
const createArtifact = firebase.functions().httpsCallable("contractors-createArtifact");

function isSignedInFor(user, contractorId) {
	return user?.uid === contractorUid(contractorId);
}

export default function ContractorExternalUploadView({ contractorId }) {
	const { t } = useTranslation();
	const user = useUser();

	const [files, setFiles] = useState([]);
	const [article, setArticle] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [uploadSuccessful, setUploadSuccessful] = useState(false);
	const [fileMarkers, setFileMarkers] = useState(true);

	const { uploadArtifactFiles, progress } = useArtifactFilesUpload();

	const onSelectFiles = async (files) => {
		setErrorMessage("");

		const nameNumbers = files.map(({ name }) => name.slice(0, 13));

		// Make sure that all file names are valid ISBN numbers
		const numbersAreValid = nameNumbers.every((number) => number.match(/^\d{13}$/));

		if (!numbersAreValid) {
			return setErrorMessage(t("fileNamesNotValidISBN", "File name(s) is not a valid ISBN"));
		}

		const fileNameNumbersMatching = nameNumbers.every((number) => nameNumbers.indexOf(number) === 0);

		if (!fileNameNumbersMatching) {
			return setErrorMessage(t("fileMustHaveSameISBN", "All files must have the same name (ISBN)"));
		}

		let article;

		try {
			({ data: article } = await findArticle({ contractorId, isbn: nameNumbers[0] }));
		} catch (error) {
			return setErrorMessage(error.message);
		}

		if (!article) {
			return setErrorMessage(t("noMatchingArticleISBN", "There is no matching article with this ISBN"));
		}

		setArticle(article);

		return setFiles(files);
	};

	const onCancel = () => {
		setArticle(null);
		setFiles([]);
		setUploadSuccessful(false);
	};

	const [onConfirm, loading, error] = useLoadingFunction(async () => {
		const artifactId = firebase.firestore().collection("artifacts").doc().id;

		const artifactFiles = await uploadArtifactFiles(artifactId, files);

		await createArtifact({
			contractorId,
			articleId: article.id,
			artifactId,
			files: artifactFiles.map(({ fullPath, name }) => ({ fullPath, name })),
			fileMarkers,
		});

		setUploadSuccessful(true);
	});

	return (
		<PageView header={t("externalUpload", "External upload")} maxWidth={600}>
			{!isSignedInFor(user, contractorId) ? (
				<ContractorExternalUploadLogin contractorId={contractorId} />
			) : uploadSuccessful ? (
				<>
					<p>
						Files successfully uploaded to <strong>{article.name}</strong>!
					</p>

					<a onClick={onCancel} href="#">
						{t("backToUpload", "Back to upload")}
					</a>
				</>
			) : files.length > 0 ? (
				<>
					<p>
						These files will be uploaded to <strong>{article.name}</strong>
					</p>

					<FileUploadTable files={files} progress={progress} />

					<Flex style={{ marginTop: "1rem" }}>
						<Flex>
							<Checkbox
								label={t("setFileChapters", "Set file chapters")}
								checked={fileMarkers}
								onChange={() => setFileMarkers((fileMarkers) => !fileMarkers)}
							/>
						</Flex>

						<Flex justifyContent="flex-end" gap={10}>
							<Button onClick={onCancel} disabled={loading}>
								{t("cancel", "Cancel")}
							</Button>

							<PrimaryButton onClick={onConfirm} disabled={loading} loading={loading}>
								{t("confirm", "Confirm")}
							</PrimaryButton>
						</Flex>
					</Flex>
				</>
			) : (
				<>
					{t("externalUploadInformation")
						.split("\n")
						.map((row, index) => (
							<p key={index}>{row}</p>
						))}

					{(errorMessage || error) && <ErrorMessage error={errorMessage || error} />}

					<FileDropzone accept={getAcceptedArtifactFiles()} onChange={onSelectFiles} />
				</>
			)}
		</PageView>
	);
}
